import { useTranslations } from '@aikoa/i18n/react'
import { type ReactNode, useMemo, useState } from 'react'
import { View } from 'react-native'
import { useShallow } from 'zustand/react/shallow'
import { ActionButton } from '~/components/Actions'
import { LeadManualFilter } from '~/components/leads/LeadManualFilter'
import { LeadOfferedAsset } from '~/components/leads/LeadOfferedAsset'
import { useLeadStore, type LeadFilterMode } from '~/components/leads/leadStore'
import { Heading1 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button, type ButtonSize, type ButtonVariant } from '~/components/ui/button'
import { TabBar, TabContent, Tabs, type TabItem } from '~/components/ui/tabs'
import { propertyFilterFormObjectSchema } from '~/lib/propertyFormSchemas'
import { cn } from '~/lib/utils'

export function LeadsFilterBottomSheet({
  triggerClassName,
  triggerVariant,
  triggerSize,
  triggerLabel,
}: {
  triggerClassName?: string
  triggerVariant?: ButtonVariant
  triggerSize?: ButtonSize
  triggerLabel?: string | ReactNode
}) {
  const { t } = useTranslations('leads')
  const [isOpen, setIsOpen] = useState(false)

  const { activeFilterMode, setActiveFilterMode, manualFilter } = useLeadStore(
    useShallow((state) => ({
      activeFilterMode: state.activeFilterMode,
      setActiveFilterMode: state.setActiveFilterMode,
      manualFilter: state.manualFilter,
    }))
  )

  const hasInvalidFilters = useMemo(() => {
    const { filters } = manualFilter
    const hasAnyFilter =
      filters.priceMin !== undefined ||
      filters.priceMax !== undefined ||
      filters.sizeMin !== undefined ||
      filters.sizeMax !== undefined ||
      filters.roomsMin !== undefined ||
      filters.roomsMax !== undefined

    if (!hasAnyFilter) {
      return false
    }

    if (!propertyFilterFormObjectSchema.partial().safeParse(filters).success) {
      return true
    }

    return (
      (filters.priceMin !== undefined &&
        filters.priceMax !== undefined &&
        filters.priceMin > filters.priceMax) ||
      (filters.sizeMin !== undefined &&
        filters.sizeMax !== undefined &&
        filters.sizeMin > filters.sizeMax) ||
      (filters.roomsMin !== undefined &&
        filters.roomsMax !== undefined &&
        filters.roomsMin > filters.roomsMax)
    )
  }, [manualFilter])

  const filterTabs = useMemo<TabItem<LeadFilterMode>[]>(
    () => [
      {
        id: 'manual',
        label: t(($) => $.detail.tabs.interest),
        content: <LeadManualFilter className="px-6 pt-4" />,
      },
      {
        id: 'asset',
        label: t(($) => $.detail.offeredAsset),
        content: <LeadOfferedAsset className="px-6 pb-6 pt-4" />,
      },
    ],
    [t]
  )

  return (
    <>
      <ActionButton
        action={{
          key: 'activate-leads-filter',
          label: triggerLabel ?? t(($) => $.filters.activate),
          buttonVariant: triggerVariant ?? 'link-primary',
          buttonSize: triggerSize ?? 'default',
          onPress: () => setIsOpen(true),
        }}
        className={cn('self-center', triggerClassName)}
      />
      <BottomSheet modalKey="leads-filter" isOpen={isOpen} onOpenChange={setIsOpen}>
        <View className="gap-6 pb-6">
          <Heading1 className="text-center">{t(($) => $.filters.title)}</Heading1>
          <Tabs tabs={filterTabs} activeTab={activeFilterMode} setActiveTab={setActiveFilterMode}>
            <View className="gap-4">
              <TabBar
                className="border-b border-secondary px-6"
                tabButtonClassName="rounded-content-inner pb-2 rounded-b-none border border-transparent aria-selected:border-secondary aria-selected:border-b-white aria-selected:translate-y-[2px] bg-white"
              />
              <TabContent className="px-0" tabContentClassName="bg-white" />
            </View>
          </Tabs>
          <Button
            variant="primary"
            size="lg"
            onPress={() => setIsOpen(false)}
            disabled={hasInvalidFilters}>
            {t(($) => $.filters.showResults)}
          </Button>
        </View>
      </BottomSheet>
    </>
  )
}
